import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FormBuilderState, FormSection } from '../types';
import { getTaskForm } from '../api/getTaskFormApiService';
import { updateTaskForm } from '../api/updateTaskFormApiService';
import { mapApiFormToBuilderState } from '../utils/mapApiFormToBuilderState';
import { TASK_FORM_LABELS } from '../constants/labels';
import {
  validateForPreview,
  validateForm,
  hasErrors,
  clearResolvedSectionErrors,
  makeEmptyField,
  makeEmptySection,
  type FormErrors,
} from './useCreateForm';

export function useEditForm(id: string) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [formId, setFormId] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [formState, setFormState] = useState<FormBuilderState | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isErrorSnackbarOpen, setIsErrorSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [validationAttemptCount, setValidationAttemptCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    setLoadError('');

    getTaskForm(id).then((result) => {
      if (!mounted) {
        return;
      }
      if (result.success && result.data) {
        setFormId(result.data.form_id);
        setFormState(mapApiFormToBuilderState(result.data));
      } else if (result.notFound) {
        setNotFound(true);
      } else {
        setLoadError(result.error ?? TASK_FORM_LABELS.EDIT_ERROR_GENERIC);
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [id]);

  // ─── State mutators ────────────────────────────────────────────────────────

  const setName = useCallback((name: string) => {
    setIsDirty(true);
    setFormState((prev) => (prev ? { ...prev, name } : prev));
    setErrors((prev) => {
      if (!prev.name) {
        return prev;
      }
      const { name: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const setDescription = useCallback((description: string) => {
    setIsDirty(true);
    setFormState((prev) => (prev ? { ...prev, description } : prev));
    setErrors((prev) => {
      if (!prev.description || !description.trim()) {
        return prev;
      }
      const { description: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const addSection = useCallback(() => {
    setIsDirty(true);
    setFormState((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        sections: [
          ...prev.sections,
          { ...makeEmptySection(), fields: [makeEmptyField()] },
        ],
      };
    });
  }, []);

  const updateSection = useCallback((idx: number, updated: FormSection) => {
    setIsDirty(true);
    setFormState((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        sections: prev.sections.map((s, i) => (i === idx ? updated : s)),
      };
    });
    setErrors((prev) => {
      const sectionErr = prev.sections?.[updated.id];
      if (!sectionErr) {
        return prev;
      }
      const newSectionErr = clearResolvedSectionErrors(sectionErr, updated);
      return {
        ...prev,
        sections: { ...prev.sections, [updated.id]: newSectionErr },
      };
    });
  }, []);

  const removeSection = useCallback((idx: number) => {
    setIsDirty(true);
    setFormState((prev) => {
      if (!prev) {
        return prev;
      }
      return { ...prev, sections: prev.sections.filter((_, i) => i !== idx) };
    });
  }, []);

  const reorderSections = useCallback((fromIdx: number, toIdx: number) => {
    setIsDirty(true);
    setFormState((prev) => {
      if (!prev) {
        return prev;
      }
      const sections = [...prev.sections];
      const a = sections[fromIdx];
      const b = sections[toIdx];
      if (!a || !b) {
        return prev;
      }
      sections[fromIdx] = b;
      sections[toIdx] = a;
      return { ...prev, sections };
    });
  }, []);

  // ─── Submit ────────────────────────────────────────────────────────────────

  const executeSubmit = useCallback(async () => {
    if (!formState) {
      return;
    }
    const validationErrors = validateForm(formState);
    if (hasErrors(validationErrors)) {
      setErrors(validationErrors);
      return;
    }
    setIsSubmitting(true);
    setErrors({});
    const result = await updateTaskForm(id, formState);
    setIsSubmitting(false);
    if (result.success) {
      navigate('/Formularze-zadań', {
        state: { navigationSuccessMessage: TASK_FORM_LABELS.EDIT_SUCCESS },
      });
    } else {
      setSnackbarError(result.error ?? TASK_FORM_LABELS.EDIT_ERROR_GENERIC);
      setIsErrorSnackbarOpen(true);
    }
  }, [formState, id, navigate]);

  const handleSaveFromPreview = executeSubmit;

  // ─── Cancel ───────────────────────────────────────────────────────────────

  const handleCancel = useCallback(() => {
    if (isDirty) {
      setIsCancelModalOpen(true);
    } else {
      navigate('/Formularze-zadań');
    }
  }, [isDirty, navigate]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    navigate('/Formularze-zadań');
  }, [navigate]);

  // ─── Preview ──────────────────────────────────────────────────────────────

  const handleOpenPreview = useCallback(() => {
    if (!formState) {
      return;
    }
    const validationErrors = validateForPreview(formState);
    if (hasErrors(validationErrors)) {
      setErrors(validationErrors);
      setValidationAttemptCount((n) => n + 1);
      return;
    }
    setErrors({});
    setIsPreviewOpen(true);
  }, [formState]);

  const handleClosePreview = useCallback(() => setIsPreviewOpen(false), []);

  return {
    loading,
    formId,
    notFound,
    loadError,
    formState,
    errors,
    isSubmitting,
    isCancelModalOpen,
    isErrorSnackbarOpen,
    snackbarError,
    isPreviewOpen,
    validationAttemptCount,
    setName,
    setDescription,
    addSection,
    updateSection,
    removeSection,
    reorderSections,
    handleSaveFromPreview,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleOpenPreview,
    handleClosePreview,
    handleCloseErrorSnackbar: () => setIsErrorSnackbarOpen(false),
  };
}
